import { getGlobalHeadersAsObject } from "./global-headers";

/** Flow metrics for one message queue — see core/endpoints/queue_health.py. */
export interface QueueFlowHealth {
	/** Queued, unsent, no error. */
	backlog: number;
	/** Of those, the ones past the cutoff — what `?stuck=true` returns. */
	stuck: number;
	sent_1h: number;
	sent_24h: number;
	sent_window: number;
	window_days: number;
	last_sent_at: string | null;
	idle_hours: number | null;
	oldest_backlog_at: string | null;
	oldest_backlog_hours: number | null;
	/** Hours for the backlog to clear at the queue's own average rate. */
	drain_hours: number | null;
	/** Rows waiting and nothing sent in 24h. */
	stalled: boolean;
	/** Queue is moving, but its oldest row waited far longer than throughput explains. */
	aging: boolean;
	alert: boolean;
}

export interface ExtractionHealthCheck {
	metric: string;
	value_1h: number;
	value_24h: number;
	floor_1h: number;
	floor_24h: number;
	alert_1h: boolean;
	alert_24h: boolean;
	alert: boolean;
}

export interface ExtractionQualityCheck {
	metric: string;
	label: string;
	pct_24h: number;
	ceiling_pct: number;
	alert: boolean;
}

export interface ExtractionHealth {
	healthy: boolean;
	checks: ExtractionHealthCheck[];
	quality: ExtractionQualityCheck[];
}

const fetchJson = async <T>(path: string): Promise<T> => {
	const response = await fetch(path, {
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
	}
	return response.json();
};

export const fetchEmailQueueHealth = (): Promise<QueueFlowHealth> =>
	fetchJson<QueueFlowHealth>("/api/v2/queue-health/email/");

export const fetchLinkedinQueueHealth = (): Promise<QueueFlowHealth> =>
	fetchJson<QueueFlowHealth>("/api/v2/queue-health/linkedin/");

export const fetchExtractionHealth = (): Promise<ExtractionHealth> =>
	fetchJson<ExtractionHealth>("/api/v2/queue-health/extraction/");

export interface CeleryErrorGroup {
	task_name: string;
	count: number;
	exception: string;
}

export interface CeleryErrorsHealth {
	unsent: number;
	threshold: number;
	groups: CeleryErrorGroup[];
}

export const fetchCeleryErrorsHealth = (): Promise<CeleryErrorsHealth> =>
	fetchJson<CeleryErrorsHealth>("/api/v2/queue-health/celery-errors/");

export interface PluginWorkLogHealth {
	errors: number;
	stuck: number;
	threshold: number;
}

export const fetchPluginWorkLogHealth = (): Promise<PluginWorkLogHealth> =>
	fetchJson<PluginWorkLogHealth>("/api/v2/queue-health/plugin-work-log/");

export interface DbBackupHealth {
	age_hours: number | null;
	threshold_hours: number;
	alert: boolean;
}

export const fetchDbBackupHealth = (): Promise<DbBackupHealth> =>
	fetchJson<DbBackupHealth>("/api/v2/queue-health/db-backup/");

export interface GpuBoxHealth {
	/** The box's router URL while its heartbeat is fresh, else null. */
	url: string | null;
	/** Seconds since the last heartbeat; null when no box ever registered. */
	last_seen_seconds: number | null;
	alert: boolean;
}

export const fetchGpuBoxHealth = (): Promise<GpuBoxHealth> =>
	fetchJson<GpuBoxHealth>("/api/v2/queue-health/gpu-box/");

export interface EsIndexHealth {
	/** Index names that do not exist in this environment. */
	missing: string[];
	es_enabled: boolean;
	alert: boolean;
}

export const fetchEsIndexHealth = (): Promise<EsIndexHealth> =>
	fetchJson<EsIndexHealth>("/api/v2/queue-health/es-index/");
export interface MemberIdentity {
	member_id: number;
	name: string | null;
	url: string | null;
}

export interface CookieMismatch {
	username: string;
	bot_id: number;
	session_id: number;
	expected: MemberIdentity;
	li_at: MemberIdentity;
	verification: "mismatch" | "enterprise_mismatch";
}

export interface CookieMismatchHealth {
	count: number;
	mismatches: CookieMismatch[];
}

export const fetchCookieMismatchHealth = (): Promise<CookieMismatchHealth> =>
	fetchJson<CookieMismatchHealth>("/api/v2/queue-health/cookie-mismatch/");

/** One failure shape across the unprocessed Jobylon events that carry it. */
export interface JobylonErrorGroup {
	/** The recorded error, cut to the part that identifies which failure it is. */
	error: string;
	count: number;
	latest: string;
}

/** One customer's Jobylon job feed, and when it was last read. */
export interface JobylonFeedHealth {
	company: string;
	active: boolean;
	last_polled: string | null;
	last_error: string;
}

/** How far back the status page's chart looks; "all" starts at the first event. */
export const JOBYLON_TIMELINE_RANGES = ["24h", "7d", "30d", "1y", "all"] as const;
export type JobylonTimelineRange = (typeof JOBYLON_TIMELINE_RANGES)[number];

/** What one bar covers; the server picks it from how long the range is. */
export type JobylonTimelineUnit = "hour" | "day" | "week" | "month";

/** One bar of the chart — an hour, day, week or month — what Jobylon sent, and what ODI finished. */
export interface JobylonBucket {
	start: string;
	received: number;
	processed: number;
	/** Of `received`: new applications whose CV came on the Partner event. */
	applications_with_cv: number;
	/** Of `received`: new applications no Partner event has brought a CV for. */
	applications_without_cv: number;
	/** Of `received`: the Partner events themselves, one CV each — bulk sends included. */
	cvs_received: number;
	/** Of `received`: an application moved to another stage. */
	stage_changes: number;
	/** Of `received`: a rejection sent, or the reason recorded for one. */
	rejections: number;
	/** Of `received`: an application deleted in Jobylon. */
	applications_deleted: number;
	/** Of `received`: a job created, updated or opened/closed, on either track. */
	job_changes: number;
	/** Of `received`: none of the above — a kind of event nothing names yet. */
	other: number;
}

/**
 * One imported Jobylon job's recent applications, and how many brought their CV.
 * The CV comes only on the Partner event, which Jobylon sends only for jobs with
 * the "Send to Partner" action.
 */
export interface JobylonJobDocuments {
	/** The customer whose feed the job came from; null for a job first seen on a webhook. */
	company: string | null;
	job_id: number;
	title: string;
	assignment_id: number;
	applications: number;
	with_documents: number;
	without_documents: number;
	/** Jobylon's `from_date`: the day the ad is published from (YYYY-MM-DD). */
	published: string;
	/** When the job's newest application arrived. */
	latest_application: string;
	/** Its CV came on arrival — the job carries the "Send to Partner" action now. */
	latest_with_cv: boolean;
}

/** The Jobylon integration's state — see core/endpoints/jobylon_health.py. */
export interface JobylonIngestHealth {
	received_24h: number;
	processed_24h: number;
	/** Processed within the last sweep interval — how fast the drain is moving now. */
	processed_recent: number;
	/** Stored events still waiting to become ODI records; the sweep retries these. */
	backlog: number;
	/** Backlog rows the sweep dispatched and the worker has not finished yet. */
	in_flight: number;
	/** Backlog rows whose stored file URLs have expired, waiting on a re-fetch. */
	awaiting_file_refetch: number;
	/** Rows the sweep gave up on: attempts at the cap. */
	exhausted: number;
	oldest_unprocessed: string | null;
	last_event_received: string | null;
	last_processed: string | null;
	last_sweep: string | null;
	last_sweep_redispatched: number;
	/** The range `timeline` covers; the cards above it stay on the last 24 h. */
	timeline_range: JobylonTimelineRange;
	timeline_unit: JobylonTimelineUnit;
	/** One bucket per `timeline_unit` over the range, oldest first; empty ones included. */
	timeline: JobylonBucket[];
	errors: JobylonErrorGroup[];
	feeds: JobylonFeedHealth[];
	/** True when the banner should alert: the backlog is aging, or rows gave up. */
	alert: boolean;
	/** How far back `job_documents` looks. */
	documents_window_hours: number;
	job_documents: JobylonJobDocuments[];
	/** The same table over the timeline's span — the status page's, beside its chart. */
	timeline_job_documents: JobylonJobDocuments[];
	/** True when any job had an application arrive without its CV in the window. */
	missing_partner_action: boolean;
	/** The banner's alert: `missing_partner_action`, for superusers only. */
	partner_action_alert: boolean;
}

export const fetchJobylonIngestHealth = (
	range: JobylonTimelineRange = "24h"
): Promise<JobylonIngestHealth> =>
	fetchJson<JobylonIngestHealth>(`/api/v2/queue-health/jobylon/?range=${range}`);
